export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface StatusMeta {
  label: string;
  tone: StatusTone;
}

export const STATUS_MAP: Record<string, StatusMeta> = {
  eventregistered: { label: 'Registered', tone: 'success' },
  eventwaitlisted: { label: 'Waitlisted', tone: 'warning' },
  eventcancelled: { label: 'Cancelled', tone: 'neutral' },
  eventattended: { label: 'Attended', tone: 'success' },
  eventnoshow: { label: 'No-show', tone: 'neutral' },
  eventblocked: { label: 'Blocked', tone: 'warning' },
  eventpending: { label: 'Pending', tone: 'warning' },
  eventnotstarted: { label: 'Not started', tone: 'neutral' },
  eventinprogress: { label: 'In progress', tone: 'info' },
  eventprocessing: { label: 'Processing', tone: 'info' },
  eventsuppressed: { label: 'Suppressed', tone: 'neutral' },
  eventtestmode: { label: 'Test mode', tone: 'warning' },
  eventcompleted: { label: 'Completed', tone: 'success' },
  eventfailed: { label: 'Failed', tone: 'danger' },
  eventneedsreview: { label: 'Needs review', tone: 'warning' },
  eventresolved: { label: 'Resolved', tone: 'success' },
  eventunabletoresolve: { label: 'Unable to resolve', tone: 'neutral' },
  eventdismissed: { label: 'Dismissed', tone: 'neutral' },
  eventduplicate: { label: 'Duplicate', tone: 'neutral' },
  // Event and activity phases are derived from real start/end times and capacity counts.
  eventlive: { label: 'Live', tone: 'success' },
  eventupcoming: { label: 'Upcoming', tone: 'neutral' },
  eventended: { label: 'Ended', tone: 'neutral' },
  eventopen: { label: 'Open', tone: 'info' },
  eventfull: { label: 'Full', tone: 'warning' },
  eventready: { label: 'Ready', tone: 'success' },
  eventdegraded: { label: 'Needs attention', tone: 'warning' },
  eventproviderconfigured: { label: 'Configured', tone: 'success' },
  eventproviderincomplete: { label: 'Incomplete', tone: 'warning' },
  eventproviderdisabled: { label: 'Disabled', tone: 'neutral' },
  eventtouchpoint: { label: 'Touchpoint', tone: 'neutral' },
  eventpartner: { label: 'Partner', tone: 'info' },
  eventprospect: { label: 'Prospect', tone: 'info' },
  eventprivate: { label: 'Private', tone: 'neutral' },
  // Chargebee subscription status on the attendee brief (SubscriptionStatus enum values).
  eventsubactive: { label: 'Active', tone: 'success' },
  eventsubintrial: { label: 'Trial', tone: 'info' },
  eventsubfuture: { label: 'Future', tone: 'info' },
  eventsubnonrenewing: { label: 'Non-renewing', tone: 'warning' },
  eventsubpaused: { label: 'Paused', tone: 'warning' },
  eventsubcancelled: { label: 'Cancelled', tone: 'neutral' },
  emailqueued: { label: 'Queued', tone: 'neutral' },
  hubinvitequeued: { label: 'Invite pending', tone: 'neutral' },
  hubinvitesent: { label: 'Invited', tone: 'info' },
  hubinvitemember: { label: 'Already a member', tone: 'success' },
  hubinviteblocked: { label: 'Blocked', tone: 'warning' },
  hubinvitefailed: { label: 'Failed', tone: 'danger' },
  emailaccepted: { label: 'Sent to provider', tone: 'info' },
  emaildelivered: { label: 'Delivered', tone: 'success' },
  emailopened: { label: 'Open detected', tone: 'info' },
  emailbounced: { label: 'Bounced', tone: 'danger' },
  emaildropped: { label: 'Not delivered', tone: 'danger' },
  emaildeferred: { label: 'Delayed', tone: 'warning' },
  emailfailed: { label: 'Send failed', tone: 'danger' },
  hsnotlinked: { label: 'No deal linked', tone: 'neutral' },
  hsnotpublished: { label: 'Not published', tone: 'neutral' },
  hsqueued: { label: 'Update queued', tone: 'info' },
  hsupdating: { label: 'Updating HubSpot', tone: 'info' },
  hsretrying: { label: 'Retrying update', tone: 'warning' },
  hspaused: { label: 'Publishing paused', tone: 'warning' },
  hsneedsattention: { label: 'Needs attention', tone: 'danger' },
  hsunverified: { label: 'Update unconfirmed', tone: 'neutral' },
  hssynced: { label: 'Up to date', tone: 'success' },
  hswithdrawn: { label: 'Forecast withdrawn', tone: 'neutral' },
  hsanotherquote: { label: 'Another quote published', tone: 'neutral' },
  changenew: { label: 'New', tone: 'info' },
  commercialquantityincrease: { label: 'Quantity increase', tone: 'info' },
  commercialquantityreduction: { label: 'Quantity reduction', tone: 'warning' },
  commercialquantityunchanged: { label: 'Quantity unchanged', tone: 'neutral' },
  commercialamendment: { label: 'Amendment', tone: 'neutral' },
  commercialnew: { label: 'New product', tone: 'info' },
  commercialrenewal: { label: 'Renewal', tone: 'info' },
  commercialonetime: { label: 'One-time', tone: 'neutral' },
  changeexpansion: { label: 'Expansion', tone: 'success' },
  changecontraction: { label: 'Contraction', tone: 'warning' },
  changerenewal: { label: 'Renewal', tone: 'info' },
  changeunchanged: { label: 'Unchanged', tone: 'neutral' },
  changeincluded: { label: 'Included', tone: 'neutral' },
  changeonetime: { label: 'One-time', tone: 'neutral' },
  draft: { label: 'Draft', tone: 'neutral' },
  submitted: { label: 'Submitted', tone: 'info' },
  sent: { label: 'Sent', tone: 'info' },
  accepted: { label: 'Accepted', tone: 'success' },
  declined: { label: 'Declined', tone: 'danger' },
  converted: { label: 'Converted', tone: 'success' },
  opened: { label: 'Opened', tone: 'info' },
  approved: { label: 'Approved', tone: 'success' },
  sendsonapproval: { label: 'Sends on approval', tone: 'info' },
  pending: { label: 'Pending', tone: 'warning' },
  rejected: { label: 'Rejected', tone: 'danger' },
  changesrequested: { label: 'Changes requested', tone: 'warning' },
  won: { label: 'Won', tone: 'success' },
  unworked: { label: 'Unworked', tone: 'warning' },
  inprogress: { label: 'In progress', tone: 'info' },
  renewed: { label: 'Renewed', tone: 'success' },
  reduced: { label: 'Renewed · reduced', tone: 'warning' },
  saved: { label: 'Renewed · saved', tone: 'success' },
  cotermcandidate: { label: 'Co-term candidate', tone: 'info' },
  lost: { label: 'Lost', tone: 'danger' },
  open: { label: 'Open', tone: 'info' },
  revoked: { label: 'Revoked', tone: 'neutral' },
  expired: { label: 'Expired', tone: 'warning' },
  mismatch: { label: 'Mismatch', tone: 'danger' },
  halted: { label: 'Provisioning halted', tone: 'danger' },
  confirmed: { label: 'Confirmed', tone: 'success' },
  webhook: { label: 'Pending sync', tone: 'warning' },
  active: { label: 'Active', tone: 'success' },
  amending: { label: 'Amending', tone: 'info' },
  renewing: { label: 'Renewing', tone: 'info' },
  cancelling: { label: 'Cancelling', tone: 'danger' },
  legacy: { label: 'Legacy', tone: 'warning' },
  churned: { label: 'Churned', tone: 'danger' },
  atrisk: { label: 'At risk', tone: 'warning' },
  healthy: { label: 'Healthy', tone: 'success' },
  degraded: { label: 'Degraded', tone: 'warning' },
  error: { label: 'Error', tone: 'danger' },
  notconfigured: { label: 'Not configured', tone: 'neutral' },
  paid: { label: 'Paid', tone: 'success' },
  invoicepaymentdue: { label: 'Payment due', tone: 'warning' },
  invoicenotpaid: { label: 'Not paid', tone: 'danger' },
  invoiceposted: { label: 'Posted', tone: 'info' },
  invoicepending: { label: 'Pending', tone: 'neutral' },
  invoicevoided: { label: 'Voided', tone: 'neutral' },
  poc: { label: 'POC', tone: 'info' },
  trial: { label: 'Trial', tone: 'info' },
  cancelled: { label: 'Cancelled', tone: 'danger' },
  overplan: { label: 'Over plan', tone: 'danger' },
  atlimit: { label: 'At limit', tone: 'warning' },
  nearlimit: { label: 'Near limit', tone: 'warning' },
  unlimited: { label: 'Unlimited', tone: 'info' },
  newcustomer: { label: 'New customer', tone: 'info' },
  syncrequested: { label: 'Sync requested', tone: 'success' },
  namedrift: { label: 'Name drift', tone: 'warning' },
  legpending: { label: 'Queued', tone: 'neutral' },
  legcreating: { label: 'Creating', tone: 'info' },
  legcreated: { label: 'Created', tone: 'info' },
  legreconciled: { label: 'Reconciled', tone: 'success' },
  legfailed: { label: 'Failed', tone: 'danger' },
  // Co-term verdicts. Namespaced because plain `pending` already means a warning here, while a
  // co-term Pending is simply work not done yet.
  cotermpending: { label: 'Will co-term', tone: 'info' },
  cotermscheduled: { label: 'Co-termed', tone: 'success' },
  cotermskipped: { label: 'Already aligned', tone: 'success' },
  // Neutral, not warning: an excluded row is a deliberate, correct outcome (annual billing, a
  // cancelled subscription, a scheduled change we refuse to overwrite) and it carries its own
  // reason. Amber made every expected exclusion look like a fault the rep had to clear.
  cotermexcluded: { label: 'Not co-termed', tone: 'neutral' },
  cotermfailed: { label: 'Retrying', tone: 'warning' },
  cotermrefused: { label: 'Co-term failed', tone: 'danger' },
  cotermheld: { label: 'On hold', tone: 'danger' },
  engageviewed: { label: 'Viewed', tone: 'neutral' },
  engagepricing: { label: 'Read pricing', tone: 'info' },
  engagedetails: { label: 'Opened details', tone: 'info' },
  engageterms: { label: 'Reached terms', tone: 'info' },
  engageagreed: { label: 'Agreed to terms', tone: 'info' },
  engagesigning: { label: 'Started signing', tone: 'warning' },
  engagecontinued: { label: 'Continued to accept', tone: 'success' },
  engagedeclined: { label: 'Declined', tone: 'danger' },
  engagescanner: { label: 'Link scanner', tone: 'neutral' },
  engageautomation: { label: 'Automated browser', tone: 'neutral' },
  engageinternal: { label: 'Internal preview', tone: 'neutral' },
  scheduledchangechargebee: { label: 'Chargebee scheduled change', tone: 'neutral' },
  scheduledchangemeridian: { label: 'Meridian quote', tone: 'info' },
  scheduledchangeelevated: { label: 'Needs Admin or Manager', tone: 'warning' },
  scheduledchangeblocked: { label: "Can't be replaced", tone: 'danger' },
  // A quote that starts on a later day, and where its start stands once accepted.
  startson: { label: 'Starts later', tone: 'info' },
  startscheduled: { label: 'Scheduled', tone: 'info' },
  startstarted: { label: 'Started', tone: 'info' },
  startpaid: { label: 'Paid', tone: 'success' },
  startcalledoff: { label: 'Called off', tone: 'neutral' },
};

export const TONE_COLOR: Record<StatusTone, { bg: string; fg: string; dot: string }> = {
  neutral: { bg: 'var(--surface-sunken)', fg: 'var(--text-secondary)', dot: 'var(--text-tertiary)' },
  info: { bg: 'var(--accent-soft)', fg: 'var(--accent-text)', dot: 'var(--accent)' },
  success: { bg: 'var(--success-soft)', fg: 'var(--success)', dot: 'var(--success)' },
  warning: { bg: 'var(--warning-soft)', fg: 'var(--warning)', dot: 'var(--warning)' },
  danger: { bg: 'var(--danger-soft)', fg: 'var(--danger)', dot: 'var(--danger)' },
};
